import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { API_URL } from '@core/config/api-url.token';
import { API_ENDPOINTS } from '@shared/constants/api-endpoints.const';
import { Observable } from 'rxjs';
import { OnboardingStepNumber } from '../../../domain/repositories/onboarding.repository';
import {
  CheckoutStatusDto,
  OnboardingOrganizationDto,
  OnboardingTeamDraftDto,
  OnboardingPersonalDataDto,
  OnboardingPlanDto,
  OnboardingStatusDto,
  OnboardingTrialCodeRedemptionDto,
  PagedOnboardingProfessionalsDto,
  PaymentConfigDto,
  RedeemOnboardingTrialCodeRequestDto,
  SelectOnboardingPlanRequestDto,
  SaveOnboardingTeamDraftRequestDto,
  StartSubscriptionCheckoutRequestDto,
  SubscriptionCheckoutDto,
} from '../../dtos/onboarding.dto';

@Injectable()
export class OnboardingRemoteDatasource {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);

  public getStatus(): Observable<OnboardingStatusDto> {
    return this.http.get<OnboardingStatusDto>(`${this.apiUrl}${API_ENDPOINTS.onboarding.status}`);
  }

  public getPersonalData(): Observable<OnboardingPersonalDataDto> {
    return this.http.get<OnboardingPersonalDataDto>(`${this.apiUrl}${API_ENDPOINTS.onboarding.personalData}`);
  }

  public getOrganization(): Observable<OnboardingOrganizationDto> {
    return this.http.get<OnboardingOrganizationDto>(`${this.apiUrl}${API_ENDPOINTS.onboarding.organization}`);
  }

  public getStaff(): Observable<PagedOnboardingProfessionalsDto> {
    return this.http.get<PagedOnboardingProfessionalsDto>(`${this.apiUrl}${API_ENDPOINTS.onboarding.staff}`);
  }

  public getTeamDraft(): Observable<OnboardingTeamDraftDto> {
    return this.http.get<OnboardingTeamDraftDto>(`${this.apiUrl}${API_ENDPOINTS.onboarding.teamDraft}`);
  }

  public saveTeamDraft(body: SaveOnboardingTeamDraftRequestDto): Observable<OnboardingTeamDraftDto> {
    return this.http.put<OnboardingTeamDraftDto>(
      `${this.apiUrl}${API_ENDPOINTS.onboarding.teamDraft}`,
      body,
    );
  }

  public getPlans(): Observable<readonly OnboardingPlanDto[]> {
    return this.http.get<readonly OnboardingPlanDto[]>(`${this.apiUrl}${API_ENDPOINTS.onboarding.plans}`);
  }

  public selectPlan(body: SelectOnboardingPlanRequestDto): Observable<OnboardingStatusDto> {
    return this.http.post<OnboardingStatusDto>(`${this.apiUrl}${API_ENDPOINTS.onboarding.selectPlan}`, body);
  }

  public redeemTrialCode(
    body: RedeemOnboardingTrialCodeRequestDto,
  ): Observable<OnboardingTrialCodeRedemptionDto> {
    return this.http.post<OnboardingTrialCodeRedemptionDto>(
      `${this.apiUrl}${API_ENDPOINTS.onboarding.redeemTrialCode}`,
      body,
    );
  }

  public startCheckout(
    body: StartSubscriptionCheckoutRequestDto,
  ): Observable<SubscriptionCheckoutDto> {
    return this.http.post<SubscriptionCheckoutDto>(
      `${this.apiUrl}${API_ENDPOINTS.onboarding.startCheckout}`,
      body,
    );
  }

  public getCheckoutStatus(sessionId: string): Observable<CheckoutStatusDto> {
    return this.http.get<CheckoutStatusDto>(
      `${this.apiUrl}${API_ENDPOINTS.onboarding.checkoutStatus(sessionId)}`,
    );
  }

  public getPaymentConfig(): Observable<PaymentConfigDto> {
    return this.http.get<PaymentConfigDto>(
      `${this.apiUrl}${API_ENDPOINTS.onboarding.paymentConfig}`,
    );
  }

  public completeStep(stepNumber: OnboardingStepNumber): Observable<OnboardingStatusDto> {
    return this.http.post<OnboardingStatusDto>(`${this.apiUrl}${API_ENDPOINTS.onboarding.completeStep(stepNumber)}`, {});
  }
}
